import { useCallback, useMemo } from "react";
import { Platform, Pressable, View } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import type { DropdownSlotProps, InputSlotProps, SearchInputProps, SearchResult, SearchResultGroup } from "./types";
import { useCombobox } from "../../hooks/use-combobox";
import { SearchDropdown } from "./parts/SearchDropdown";
import { SearchInputField } from "./parts/SearchInputField";

// ---------------------------------------------------------------------------
// SearchInput component
// ---------------------------------------------------------------------------

function SearchInputInner<T extends SearchResult>(props: SearchInputProps<T>) {
  const {
    value,
    onChangeText,
    isLoading = false,
    placeholder = "Search…",
    label,
    onSubmit,
    textInputProps,
    leftContent,
    renderInput,
    renderDropdown,
    renderResult,
    renderLoading,
    renderEmpty,
  } = props;

  // Normalise flat results into a single anonymous group internally
  const resolvedGroups: readonly SearchResultGroup<T>[] = useMemo(() => {
    if (props.grouped) {
      return props.resultGroups;
    }
    return [{ id: "__flat__", name: "", results: props.results }];
  }, [props.grouped, props.grouped ? props.resultGroups : props.results]);

  const renderGroupHeader = props.grouped ? props.renderGroupHeader : undefined;

  const flatResults = useMemo(() => resolvedGroups.flatMap((g) => [...g.results]), [resolvedGroups]);

  const handleResultSelect = useCallback((result: T) => {
    result.onClick();
  }, []);

  const { isOpen, close, highlightedIndex, setHighlightedIndex, containerRef, handleKeyPress, handleInputFocus } =
    useCombobox<T>({
      options: flatResults,
      getOptionId: (r) => r.id,
      onSelect: handleResultSelect,
      onSubmit,
      inputValue: value,
    });

  const handleResultPress = useCallback(
    (result: T) => {
      result.onClick();
      close();
    },
    [close],
  );

  const showDropdown = isOpen && (isLoading || flatResults.length > 0 || value.length > 0);

  const inputSlotProps: InputSlotProps = {
    value,
    onChangeText,
    onFocus: handleInputFocus,
    onKeyPress: handleKeyPress,
    placeholder,
    label,
    textInputProps,
    leftContent,
  };

  const dropdownSlotProps: DropdownSlotProps<T> = {
    resultGroups: resolvedGroups,
    isLoading,
    focusedIndex: highlightedIndex,
    onResultPress: handleResultPress,
    onResultHover: setHighlightedIndex,
    renderGroupHeader,
    renderResult,
    renderLoading,
    renderEmpty,
  };

  return (
    <View ref={containerRef} style={[styles.container, showDropdown && styles.containerOpen]}>
      {renderInput ? renderInput(inputSlotProps) : <SearchInputField {...inputSlotProps} />}

      {showDropdown && (
        <>
          {Platform.OS !== "web" && <Pressable style={styles.backdrop} onPress={close} />}
          {renderDropdown ? renderDropdown(dropdownSlotProps) : <SearchDropdown {...dropdownSlotProps} />}
        </>
      )}
    </View>
  );
}

SearchInputInner.displayName = "SearchInput";

/**
 * Generic search input with dropdown results — flat or grouped.
 *
 * Pass `grouped={true}` with `resultGroups` to show results under group
 * headers. Otherwise pass a flat `results` array for a simple list.
 *
 * @example
 * ```tsx
 * // Flat list (no groups)
 * <SearchInput
 *   value={query}
 *   onChangeText={setQuery}
 *   results={cameras}
 * />
 *
 * // Grouped list
 * <SearchInput
 *   grouped
 *   value={query}
 *   onChangeText={setQuery}
 *   resultGroups={[
 *     { id: "cameras", name: "Cameras", results: cameras },
 *     { id: "sites", name: "Sites", results: sites },
 *   ]}
 * />
 * ```
 */
const SearchInput = SearchInputInner as <T extends SearchResult = SearchResult>(
  props: SearchInputProps<T>,
) => ReturnType<typeof SearchInputInner>;

export { SearchInput };
export {
  SearchInputField,
  SearchDropdown,
  SearchGroupHeader,
  SearchResultRow,
  SearchLoading,
  SearchEmpty,
} from "./parts";
export type { SearchResultRowProps, SearchEmptyProps } from "./parts";
export type {
  SearchInputProps,
  SearchResult,
  SearchResultGroup,
  InputSlotProps,
  DropdownSlotProps,
  ResultState,
} from "./types";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  container: {
    position: "relative",
  },
  containerOpen: {
    zIndex: theme.zIndex.inputOpen,
  },
  backdrop: {
    position: "absolute",
    top: -1000,
    left: -1000,
    right: -1000,
    bottom: -1000,
    zIndex: 15,
  },
}));
